import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useAppState } from './useAppState';
import { createEmptyAppState } from '../lib/defaultState';
import { normalizeArrayNums, normalizeArrayNulls } from '../lib/array36';
import type { AppState, SaveStatus, ScenarioMeta, Charge, FreelanceCharge, SalaireCategorie, Dirigeant } from '../types';

// ─────────────────────────────────────────────────────────────
// Fixed UUID of the My Notary template (seeded via supabase-seed.sql)
// ─────────────────────────────────────────────────────────────
const TEMPLATE_ID = '00000000-0000-0000-0000-000000000001';

/** PKs (revenus, charges, …) are global — cloning must use new ids per row. */
function cloneChildRows<T extends Record<string, unknown>>(
  rows: T[] | null | undefined,
  newForecastId: string,
): Record<string, unknown>[] {
  if (!rows?.length) return [];
  return rows.map((row) => {
    const { forecast_id: _fid, id: _oldId, ...rest } = row;
    return { ...rest, id: crypto.randomUUID(), forecast_id: newForecastId };
  });
}

function isShellWithoutLineItems(s: AppState): boolean {
  if (s.revenus.length > 0 || s.salaries.length > 0) return false;
  const c = s.charges;
  return (
    c.capex.length +
      c.marketing.length +
      c.outils.length +
      c.tech.length +
      c.autres.length +
      c.freelances.length ===
    0
  );
}

// ─────────────────────────────────────────────────────────────
// Context interface
// ─────────────────────────────────────────────────────────────
interface ForecastContextValue {
  forecastId: string | null;
  forecastName: string;
  forecastDescription: string;
  saveStatus: SaveStatus;
  loading: boolean;
  scenarios: ScenarioMeta[];
  scenariosLoading: boolean;
  /** True si l'utilisateur peut écrire sur le scénario actif (owner, editor, admin, super_admin). */
  canWrite: boolean;
  /** Niveau d'accès effectif sur le scénario actif. */
  currentAccessLevel: 'owner' | 'viewer' | 'editor' | 'admin';
  saveNow: () => Promise<void>;
  refreshFromDB: () => Promise<void>;
  rename: (name: string) => Promise<void>;
  resetForecast: () => Promise<void>;
  switchScenario: (id: string) => Promise<void>;
  createScenario: (name: string, description: string, copyFromId?: string) => Promise<void>;
  deleteScenario: (id: string) => Promise<void>;
  updateScenarioMeta: (id: string, name: string, description: string) => Promise<void>;
}

export const ForecastContext = createContext<ForecastContextValue | null>(null);

// ─────────────────────────────────────────────────────────────
// DB → AppState assembly
// ─────────────────────────────────────────────────────────────

function mapCharge(row: Record<string, unknown>, n: number): Charge {
  return {
    id: row.id as string,
    libelle: row.libelle as string,
    mode: row.mode as Charge['mode'],
    montantUniforme: Number(row.montant_uniforme),
    montantsMensuels: normalizeArrayNums(row.montants_mensuels, n),
    montantsReels: normalizeArrayNulls(row.montants_reels, n),
    progStart: Number(row.prog_start),
    progBase: Number(row.prog_base),
    progPct: Number(row.prog_pct),
    progPctExercices: parsePctExercices(row.prog_pct_exercices),
    variableRevenuId: (row.variable_revenu_id as string | null) ?? null,
    variableChargeId: (row.variable_charge_id as string | null) ?? null,
    variablePct: Number(row.variable_pct ?? 0),
    variablePctExercices: parsePctExercices(row.variable_pct_exercices),
    variablePctMensuels: parsePctExercices(row.variable_pct_mensuels),
    finMois: row.fin_mois != null ? Number(row.fin_mois) : null,
    notes: row.notes != null ? String(row.notes) : '',
    deductible: row.deductible != null ? Boolean(row.deductible) : true,
    tauxTVA: Number(row.taux_tva ?? 20),
  };
}

function parsePctExercices(raw: unknown): number[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const arr = raw
    .map((v) => (typeof v === 'number' ? v : Number(v)))
    .map((v) => (Number.isFinite(v) ? v : 0));
  return arr.length > 0 ? arr : null;
}

function parseSalaireCategories(raw: unknown): SalaireCategorie[] {
  if (!Array.isArray(raw)) return [];
  const out: SalaireCategorie[] = [];
  for (const x of raw) {
    if (x == null || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    const id = o.id != null ? String(o.id) : '';
    const label = o.label != null ? String(o.label).trim() : '';
    if (id) out.push({ id, label: label || 'Catégorie' });
  }
  return out;
}

function mapDirigeantRow(row: Record<string, unknown>, n: number): Dirigeant {
  const div = row.dividendes as number[] | undefined;
  return {
    id: String(row.id),
    nom: (row.nom as string) ?? '',
    notes: row.notes != null ? String(row.notes) : '',
    modeSaisie: (['brut','net','cout'].includes(row.mode_saisie as string) ? row.mode_saisie : (row.mode_net ? 'net' : 'brut')) as 'brut'|'net'|'cout',
    regime: (row.regime as string) ?? 'custom',
    customTaux: Number(row.custom_taux),
    remuMode: Boolean(row.remu_mode),
    remuUniform: Number(row.remu_uniform),
    remuMensuels: normalizeArrayNums(row.remu_mensuels, n),
    remuReels: normalizeArrayNulls(row.remu_reels, n),
    dividendes: Array.isArray(div) && div.length >= 3 ? [div[0], div[1], div[2]] : [0, 0, 0],
  };
}

function mapFreelance(row: Record<string, unknown>, n: number): FreelanceCharge {
  return {
    id: row.id as string,
    nom: row.nom as string,
    mission: row.mission as string,
    mode: row.mode as FreelanceCharge['mode'],
    montantUniforme: Number(row.montant_uniforme),
    montantsMensuels: normalizeArrayNums(row.montants_mensuels, n),
    montantsReels: normalizeArrayNulls(row.montants_reels, n),
    progStart: Number(row.prog_start),
    progBase: Number(row.prog_base),
    progPct: Number(row.prog_pct),
    progPctExercices: parsePctExercices(row.prog_pct_exercices),
    variableRevenuId: (row.variable_revenu_id as string | null) ?? null,
    variableChargeId: (row.variable_charge_id as string | null) ?? null,
    variablePct: Number(row.variable_pct ?? 0),
    variablePctExercices: parsePctExercices(row.variable_pct_exercices),
    variablePctMensuels: parsePctExercices(row.variable_pct_mensuels),
    finMois: row.fin_mois != null ? Number(row.fin_mois) : null,
    notes: row.notes != null ? String(row.notes) : '',
    deductible: row.deductible != null ? Boolean(row.deductible) : true,
    tauxTVA: Number(row.taux_tva ?? 20),
  };
}

function assembleAppState(
  forecast: Record<string, unknown>,
  revenus: Record<string, unknown>[],
  chargesRows: Record<string, unknown>[],
  freelancesRows: Record<string, unknown>[],
  salariesRows: Record<string, unknown>[],
  dirigeantsRows: Record<string, unknown>[],
  legacyDirigeantRow: Record<string, unknown> | null,
  fiscaliteRow: Record<string, unknown> | null,
  autresTaxesRows: Record<string, unknown>[],
  reelRow: Record<string, unknown> | null,
  investissementsRows: Record<string, unknown>[] = [],
  notairesConfigRow: Record<string, unknown> | null = null,
): AppState {
  const empty = createEmptyAppState();

  const ex1 = Math.max(1, Math.round(Number(forecast.exercice1_duree ?? 12)));
  const ex2 = Math.max(1, Math.round(Number(forecast.exercice2_duree ?? 12)));
  const ex3 = Math.max(1, Math.round(Number(forecast.exercice3_duree ?? 12)));
  const totalMi = ex1 + ex2 + ex3;

  const byCategorie = (cat: string) =>
    chargesRows
      .filter((c) => c.categorie === cat)
      .sort((a, b) => Number(a.position) - Number(b.position))
      .map((r) => mapCharge(r, totalMi));

  return {
    meta: {
      tresoInitiale: Number(forecast.treso_initiale ?? 0),
      societeNom: (forecast.societe_nom as string) ?? '',
      societeActivite: (forecast.societe_activite as string) ?? '',
      societeMarches: (forecast.societe_marches as string) ?? '',
      societeCreation: (forecast.societe_creation as string) ?? '',
      exercice1Duree: ex1,
      exercice2Duree: ex2,
      exercice3Duree: ex3,
      logoUrl: (forecast.logo_url as string) ?? '',
      primaryColor: (forecast.primary_color as string) ?? '#313131',
      formeJuridique: (forecast.forme_juridique as string as any) ?? 'SASU',
      tauxIS: Number(forecast.taux_is ?? 25),
      tauxISReduit: Number(forecast.taux_is_reduit ?? 15),
      seuilISReduit: Number(forecast.seuil_is_reduit ?? 42500),
      tauxPFU: Number(forecast.taux_pfu ?? 30),
      defaultTauxSalarie: Number(forecast.default_taux_salarie ?? 42),
      defaultTauxDirigeant: Number(forecast.default_taux_dirigeant ?? 45),
      caScenarioPct: Number(forecast.ca_scenario_pct ?? 100),
    },
    revenus: revenus
      .sort((a, b) => Number(a.position) - Number(b.position))
      .map((r) => ({
        id: r.id as string,
        libelle: r.libelle as string,
        categorie: r.categorie as AppState['revenus'][0]['categorie'],
        mode: r.mode as AppState['revenus'][0]['mode'],
        montantUniforme: Number(r.montant_uniforme),
        debutMois: Number(r.debut_mois),
        montantsMensuels: normalizeArrayNums(r.montants_mensuels, totalMi),
        montantsReels: normalizeArrayNulls(r.montants_reels, totalMi),
        progStart: Number(r.prog_start),
        progBase: Number(r.prog_base),
        progPct: Number(r.prog_pct),
        progPctMensuels: normalizeArrayNums(r.prog_pct_mensuels, totalMi),
        notes: r.notes != null ? String(r.notes) : '',
      })),
    charges: {
      capex: byCategorie('capex'),
      marketing: byCategorie('marketing'),
      outils: byCategorie('outils'),
      tech: byCategorie('tech'),
      banque: byCategorie('banque'),
      legal: byCategorie('legal'),
      locaux: byCategorie('locaux'),
      vehicules: byCategorie('vehicules'),
      freelances: freelancesRows
        .sort((a, b) => Number(a.position) - Number(b.position))
        .map((r) => mapFreelance(r, totalMi)),
      autres: byCategorie('autres'),
    },
    salaireCategories: parseSalaireCategories(forecast.salaire_categories),
    salaries: salariesRows
      .sort((a, b) => Number(a.position) - Number(b.position))
      .map((s) => ({
        id: s.id as string,
        nom: s.nom as string,
        poste: s.poste as string,
        categorieId:
          s.categorie_id != null && String(s.categorie_id).trim() !== ''
            ? String(s.categorie_id)
            : null,
        moisEntree: Number(s.mois_entree),
        moisSortie: s.mois_sortie != null ? Number(s.mois_sortie) : null,
        brut: Number(s.brut),
        tauxPatronal: s.taux_patronal as AppState['salaries'][0]['tauxPatronal'],
        customTaux: Number(s.custom_taux),
        primes: normalizeArrayNums(s.primes, totalMi),
        brutsReels: normalizeArrayNulls(s.bruts_reels, totalMi),
        notes: s.notes != null ? String(s.notes) : '',
      })),
    dirigeants: (() => {
      let list = dirigeantsRows
        .sort((a, b) => Number(a.position) - Number(b.position))
        .map((r) => mapDirigeantRow(r, totalMi));
      if (list.length === 0 && legacyDirigeantRow) {
        const r = legacyDirigeantRow;
        list = [
          {
            id: crypto.randomUUID(),
            nom: 'Dirigeant',
            notes: '',
            modeSaisie: (r.mode_net ? 'net' : 'brut') as 'brut'|'net'|'cout',
            regime: (r.regime as string) ?? 'custom',
            customTaux: Number(r.custom_taux),
            remuMode: Boolean(r.remu_mode),
            remuUniform: Number(r.remu_uniform),
            remuMensuels: normalizeArrayNums(r.remu_mensuels, totalMi),
            remuReels: normalizeArrayNulls(r.remu_reels, totalMi),
            dividendes: (r.dividendes as number[])?.length === 3 ? (r.dividendes as number[]) : [0, 0, 0],
          },
        ];
      }
      return list.length > 0 ? list : empty.dirigeants;
    })(),
    fiscalite: {
      tauxReduitPME: Boolean(fiscaliteRow?.taux_reduit_pme ?? true),
      cfe: (fiscaliteRow?.cfe as number[]) ?? [0, 0, 0],
      autresTaxes: autresTaxesRows.map((t) => ({
        id: t.id as string,
        label: t.label as string,
        montant: Number(t.montant),
        type: t.type as AppState['fiscalite']['autresTaxes'][0]['type'],
      })),
    },
    reel: reelRow
      ? {
          ca: reelRow.ca as (number | null)[],
          charges: reelRow.charges as (number | null)[],
          masseSalariale: reelRow.masse_salariale as (number | null)[],
          treso: reelRow.treso as (number | null)[],
        }
      : empty.reel,
    reelOverrides: (() => {
      // Overrides stockés en jsonb dans `reel.overrides`. Valeur attendue : Partial<ReelOverrides>
      // (chaque clé → tableau (number|null)[]). Normalisé à la longueur courante totalMi.
      const raw =
        reelRow && typeof reelRow === 'object' && 'overrides' in reelRow
          ? (reelRow as { overrides?: unknown }).overrides
          : null;
      const partial =
        raw && typeof raw === 'object' && !Array.isArray(raw)
          ? (raw as Partial<AppState['reelOverrides']>)
          : {};
      const defaults = empty.reelOverrides;
      const out = { ...defaults } as AppState['reelOverrides'];
      (Object.keys(defaults) as (keyof AppState['reelOverrides'])[]).forEach((k) => {
        out[k] = normalizeArrayNulls(partial[k], totalMi);
      });
      return out;
    })(),
    ui: {
      periode: (forecast.periode as AppState['ui']['periode']) ?? 'all',
      synthesePeriode: (forecast.synthese_periode as string) ?? 'all',
      dashboardMonthFrom:
        forecast.dashboard_month_from != null && forecast.dashboard_month_from !== ''
          ? Number(forecast.dashboard_month_from)
          : null,
      dashboardMonthTo:
        forecast.dashboard_month_to != null && forecast.dashboard_month_to !== ''
          ? Number(forecast.dashboard_month_to)
          : null,
    },
    investissements: investissementsRows.map((r) => ({
      id: r.id as string,
      libelle: (r.libelle as string) ?? '',
      moisCible: Number(r.mois_cible ?? 0),
      montant: Number(r.montant ?? 0),
      deductible: Boolean(r.deductible ?? true),
      tauxTVA: Number(r.taux_tva ?? 20),
      notes: (r.notes as string) ?? '',
    })),
    salairesBudget: normalizeArrayNums(forecast.salaires_budget, totalMi),
    salairesBudgetMode: (forecast.salaires_budget_mode as 'fixed' | 'pct') ?? 'fixed',
    salairesBudgetPct: Number(forecast.salaires_budget_pct ?? 0),
    notairesConfig: notairesConfigRow
      ? (() => {
          const panier = Number(notairesConfigRow.panier_moyen ?? 110);
          // Rétro-compat : si la nouvelle colonne `cout_partenaire_pct` existe on la prend,
          // sinon on déduit un % à partir de l'ancienne colonne en € (divisée par le panier).
          const pctRaw = notairesConfigRow.cout_partenaire_pct;
          const legacyEuros = notairesConfigRow.cout_partenaire_par_commande;
          const coutPartenairePct =
            pctRaw != null
              ? Number(pctRaw)
              : legacyEuros != null && panier > 0
                ? (Number(legacyEuros) / panier) * 100
                : 30;
          return {
            enabled: Boolean(notairesConfigRow.enabled ?? false),
            panierMoyen: panier,
            capaciteParNotaire: Number(notairesConfigRow.capacite_par_notaire ?? 150),
            salaireNotaire: Number(notairesConfigRow.salaire_notaire ?? 5000),
            notairesInitiaux: Number(notairesConfigRow.notaires_initiaux ?? 1),
            seuilRecrutementPct: Number(notairesConfigRow.seuil_recrutement_pct ?? 80),
            delaiRecrutement: Number(notairesConfigRow.delai_recrutement ?? 1),
            coutPartenairePct,
            seuilHeadOps: Number(notairesConfigRow.seuil_head_ops ?? 4),
            salaireHeadOps: Number(notairesConfigRow.salaire_head_ops ?? 6000),
            ratioAssistant: Number(notairesConfigRow.ratio_assistant ?? 6),
            salaireAssistant: Number(notairesConfigRow.salaire_assistant ?? 3500),
            sousTraitanceReels: normalizeArrayNulls(
              notairesConfigRow.sous_traitance_reels,
              totalMi,
            ),
          };
        })()
      : empty.notairesConfig,
  };
}

type TemplateBundle = {
  tpl: Record<string, unknown>;
  tplRevenus: Record<string, unknown>[] | null;
  tplCharges: Record<string, unknown>[] | null;
  tplFreelances: Record<string, unknown>[] | null;
  tplSalaries: Record<string, unknown>[] | null;
  tplDirigeants: Record<string, unknown>[] | null;
  /** Ancienne table 1:1 (migration). */
  tplDirigeantLegacy: Record<string, unknown> | null;
  tplFiscalite: Record<string, unknown> | null;
  tplAutresTaxes: Record<string, unknown>[] | null;
  tplReel: Record<string, unknown> | null;
};

type ClonedTemplateLineItems = {
  newRevenus: Record<string, unknown>[];
  newCharges: Record<string, unknown>[];
  newFreelances: Record<string, unknown>[];
  newSalaries: Record<string, unknown>[];
  newDirigeants: Record<string, unknown>[];
  newAutresTaxes: Record<string, unknown>[];
};

async function fetchTemplateBundle(): Promise<TemplateBundle | null> {
  const [
    { data: tpl },
    { data: tplRevenus },
    { data: tplCharges },
    { data: tplFreelances },
    { data: tplSalaries },
    { data: tplDirigeants },
    { data: tplDirigeantLegacy },
    { data: tplFiscalite },
    { data: tplAutresTaxes },
    { data: tplReel },
  ] = await Promise.all([
    supabase.from('forecasts').select('*').eq('id', TEMPLATE_ID).single(),
    supabase.from('revenus').select('*').eq('forecast_id', TEMPLATE_ID).order('position'),
    supabase.from('charges').select('*').eq('forecast_id', TEMPLATE_ID).order('position'),
    supabase.from('freelances').select('*').eq('forecast_id', TEMPLATE_ID).order('position'),
    supabase.from('salaries').select('*').eq('forecast_id', TEMPLATE_ID).order('position'),
    supabase.from('dirigeants').select('*').eq('forecast_id', TEMPLATE_ID).order('position'),
    supabase.from('dirigeant').select('*').eq('forecast_id', TEMPLATE_ID).maybeSingle(),
    supabase.from('fiscalite').select('*').eq('forecast_id', TEMPLATE_ID).maybeSingle(),
    supabase.from('autres_taxes').select('*').eq('forecast_id', TEMPLATE_ID),
    supabase.from('reel').select('*').eq('forecast_id', TEMPLATE_ID).maybeSingle(),
  ]);
  if (!tpl) {
    console.error('[Forecast] Modèle introuvable (id template). Exécuter supabase-seed.sql sur Supabase.');
    return null;
  }

  let tplDirigeantsResolved = (tplDirigeants ?? []) as Record<string, unknown>[];
  if (tplDirigeantsResolved.length === 0 && tplDirigeantLegacy) {
    const L = tplDirigeantLegacy;
    tplDirigeantsResolved = [
      {
        id: crypto.randomUUID(),
        forecast_id: TEMPLATE_ID,
        nom: 'Dirigeant',
        notes: '',
        mode_net: L.mode_net,
        regime: L.regime ?? 'custom',
        custom_taux: L.custom_taux,
        remu_mode: L.remu_mode,
        remu_uniform: L.remu_uniform,
        remu_mensuels: L.remu_mensuels,
        remu_reels: L.remu_reels,
        dividendes: L.dividendes,
        position: 0,
      },
    ];
  }

  return {
    tpl: tpl as Record<string, unknown>,
    tplRevenus: tplRevenus as Record<string, unknown>[] | null,
    tplCharges: tplCharges as Record<string, unknown>[] | null,
    tplFreelances: tplFreelances as Record<string, unknown>[] | null,
    tplSalaries: tplSalaries as Record<string, unknown>[] | null,
    tplDirigeants: tplDirigeantsResolved,
    tplDirigeantLegacy: tplDirigeantLegacy as Record<string, unknown> | null,
    tplFiscalite: tplFiscalite as Record<string, unknown> | null,
    tplAutresTaxes: tplAutresTaxes as Record<string, unknown>[] | null,
    tplReel: tplReel as Record<string, unknown> | null,
  };
}

function stripForecastIdRow(row: Record<string, unknown>) {
  const { forecast_id: _f, ...r } = row;
  return r;
}

function cloneTemplateLineItems(forecastId: string, b: TemplateBundle): ClonedTemplateLineItems {
  return {
    newRevenus: cloneChildRows(b.tplRevenus, forecastId),
    newCharges: cloneChildRows(b.tplCharges, forecastId),
    newFreelances: cloneChildRows(b.tplFreelances, forecastId),
    newSalaries: cloneChildRows(b.tplSalaries, forecastId),
    newDirigeants: cloneChildRows(b.tplDirigeants, forecastId),
    newAutresTaxes: cloneChildRows(b.tplAutresTaxes, forecastId),
  };
}

async function persistClonedTemplateChildren(
  forecastId: string,
  b: TemplateBundle,
  c: ClonedTemplateLineItems,
): Promise<Error | null> {
  const tasks = [];
  if (c.newRevenus.length) tasks.push(supabase.from('revenus').insert(c.newRevenus));
  if (c.newCharges.length) tasks.push(supabase.from('charges').insert(c.newCharges));
  if (c.newFreelances.length) tasks.push(supabase.from('freelances').insert(c.newFreelances));
  if (c.newSalaries.length) tasks.push(supabase.from('salaries').insert(c.newSalaries));
  if (c.newAutresTaxes.length) tasks.push(supabase.from('autres_taxes').insert(c.newAutresTaxes));
  if (c.newDirigeants.length) {
    tasks.push(supabase.from('dirigeants').insert(c.newDirigeants));
  } else if (b.tplDirigeantLegacy) {
    tasks.push(
      supabase
        .from('dirigeant')
        .insert({ ...stripForecastIdRow(b.tplDirigeantLegacy), forecast_id: forecastId }),
    );
  }
  if (b.tplFiscalite) {
    tasks.push(
      supabase.from('fiscalite').insert({ ...stripForecastIdRow(b.tplFiscalite), forecast_id: forecastId }),
    );
  }
  if (b.tplReel) {
    tasks.push(supabase.from('reel').insert({ ...stripForecastIdRow(b.tplReel), forecast_id: forecastId }));
  }
  const results = await Promise.all(tasks);
  const err = results.find((r) => r.error)?.error;
  return err ? new Error(err.message) : null;
}

function appStateFromTemplateClone(
  forecastMetaRow: Record<string, unknown>,
  b: TemplateBundle,
  c: ClonedTemplateLineItems,
): AppState {
  return assembleAppState(
    forecastMetaRow,
    c.newRevenus,
    c.newCharges,
    c.newFreelances,
    c.newSalaries,
    c.newDirigeants,
    null,
    b.tplFiscalite,
    c.newAutresTaxes,
    b.tplReel,
  );
}

/** Supprime les enfants puis recopie le modèle (nouveaux id). Met à jour la méta prévisionnel. */
async function refillForecastFromTemplate(
  forecastId: string,
  preserveUi: AppState['ui'],
): Promise<AppState | null> {
  const b = await fetchTemplateBundle();
  if (!b) return null;

  await Promise.all([
    supabase.from('revenus').delete().eq('forecast_id', forecastId),
    supabase.from('charges').delete().eq('forecast_id', forecastId),
    supabase.from('freelances').delete().eq('forecast_id', forecastId),
    supabase.from('salaries').delete().eq('forecast_id', forecastId),
    supabase.from('autres_taxes').delete().eq('forecast_id', forecastId),
    supabase.from('dirigeants').delete().eq('forecast_id', forecastId),
    supabase.from('dirigeant').delete().eq('forecast_id', forecastId),
    supabase.from('fiscalite').delete().eq('forecast_id', forecastId),
    supabase.from('reel').delete().eq('forecast_id', forecastId),
  ]);

  await supabase
    .from('forecasts')
    .update({
      treso_initiale: b.tpl.treso_initiale,
      societe_nom: b.tpl.societe_nom,
      societe_activite: b.tpl.societe_activite,
      societe_marches: b.tpl.societe_marches,
      societe_creation: b.tpl.societe_creation,
      exercice1_duree: b.tpl.exercice1_duree ?? 12,
      exercice2_duree: b.tpl.exercice2_duree ?? 12,
      exercice3_duree: b.tpl.exercice3_duree ?? 12,
      logo_url: b.tpl.logo_url ?? '',
      primary_color: b.tpl.primary_color ?? '#313131',
      forme_juridique: b.tpl.forme_juridique ?? 'SASU',
      taux_is: b.tpl.taux_is ?? 25,
      taux_is_reduit: b.tpl.taux_is_reduit ?? 15,
      seuil_is_reduit: b.tpl.seuil_is_reduit ?? 42500,
      taux_pfu: b.tpl.taux_pfu ?? 30,
      default_taux_salarie: b.tpl.default_taux_salarie ?? 42,
      default_taux_dirigeant: b.tpl.default_taux_dirigeant ?? 45,
      ca_scenario_pct: b.tpl.ca_scenario_pct ?? 100,
      salaire_categories: b.tpl.salaire_categories ?? [],
    })
    .eq('id', forecastId);

  const lineItems = cloneTemplateLineItems(forecastId, b);
  const err = await persistClonedTemplateChildren(forecastId, b, lineItems);
  if (err) {
    console.error('[Forecast] refillFromTemplate insert failed', err);
    return null;
  }

  const mergedMeta = {
    ...b.tpl,
    periode: preserveUi.periode,
    synthese_periode: preserveUi.synthesePeriode,
    dashboard_month_from: preserveUi.dashboardMonthFrom,
    dashboard_month_to: preserveUi.dashboardMonthTo,
  };
  return appStateFromTemplateClone(mergedMeta, b, lineItems);
}

// ─────────────────────────────────────────────────────────────
// Load all related data for a forecast in parallel
// ─────────────────────────────────────────────────────────────
/**
 * Returns null when at least one critical query returned a DB error
 * (e.g. stale PostgREST schema cache after ALTER TABLE).
 * The caller must NOT trigger refillForecastFromTemplate in that case.
 */
async function fetchForecastData(
  forecastRow: Record<string, unknown>,
): Promise<AppState | null> {
  const forecastId = forecastRow.id as string;

  const [
    { data: revenus, error: eRev },
    { data: charges, error: eChg },
    { data: freelances, error: eFl },
    { data: salaries, error: eSal },
    { data: dirigeantsRows, error: eDirig },
    { data: legacyDirigeant },
    { data: fiscalite },
    { data: autresTaxes },
    { data: reel },
    { data: investissementsRows, error: eInv },
    { data: notairesConfig, error: eNot },
  ] = await Promise.all([
    supabase.from('revenus').select('*').eq('forecast_id', forecastId).order('position'),
    supabase.from('charges').select('*').eq('forecast_id', forecastId).order('position'),
    supabase.from('freelances').select('*').eq('forecast_id', forecastId).order('position'),
    supabase.from('salaries').select('*').eq('forecast_id', forecastId).order('position'),
    supabase.from('dirigeants').select('*').eq('forecast_id', forecastId).order('position'),
    supabase.from('dirigeant').select('*').eq('forecast_id', forecastId).maybeSingle(),
    supabase.from('fiscalite').select('*').eq('forecast_id', forecastId).maybeSingle(),
    supabase.from('autres_taxes').select('*').eq('forecast_id', forecastId),
    supabase.from('reel').select('*').eq('forecast_id', forecastId).maybeSingle(),
    supabase.from('investissements').select('*').eq('forecast_id', forecastId).order('position'),
    supabase.from('notaires_config').select('*').eq('forecast_id', forecastId).maybeSingle(),
  ]);

  // If any critical table returned an error (e.g. stale schema cache),
  // abort and return null so the caller does NOT trigger data erasure.
  if (eRev || eChg || eFl || eSal) {
    console.error('[Forecast] fetchForecastData error:', { eRev, eChg, eFl, eSal });
    return null;
  }
  // Non-critical tables: log errors but continue loading
  if (eDirig) console.warn('[Forecast] dirigeants load error (non-blocking):', eDirig.message);
  if (eInv) console.warn('[Forecast] investissements load error (table may not exist yet):', eInv.message);
  if (eNot) console.warn('[Forecast] notaires_config load error (table may not exist yet, run supabase-patch-notaires.sql):', eNot.message);

  return assembleAppState(
    forecastRow,
    (revenus ?? []) as Record<string, unknown>[],
    (charges ?? []) as Record<string, unknown>[],
    (freelances ?? []) as Record<string, unknown>[],
    (salaries ?? []) as Record<string, unknown>[],
    (dirigeantsRows ?? []) as Record<string, unknown>[],
    (legacyDirigeant ?? null) as Record<string, unknown> | null,
    (fiscalite ?? null) as Record<string, unknown> | null,
    (autresTaxes ?? []) as Record<string, unknown>[],
    (reel ?? null) as Record<string, unknown> | null,
    (investissementsRows ?? []) as Record<string, unknown>[],
    (notairesConfig ?? null) as Record<string, unknown> | null,
  );
}

// ─────────────────────────────────────────────────────────────
// Copy template forecast to a new user forecast
// ─────────────────────────────────────────────────────────────
async function copyTemplateForUser(
  userId: string,
): Promise<{ id: string; name: string; appState: AppState } | null> {
  const b = await fetchTemplateBundle();
  if (!b) return null;
  const { tpl } = b;

  const { data: newForecast, error: fErr } = await supabase
    .from('forecasts')
    .insert({
      user_id: userId,
      name: 'My Notary — Prévisionnel',
      is_template: false,
      treso_initiale: tpl.treso_initiale,
      societe_nom: tpl.societe_nom,
      societe_activite: tpl.societe_activite,
      societe_marches: tpl.societe_marches,
      societe_creation: tpl.societe_creation,
      exercice1_duree: tpl.exercice1_duree ?? 12,
      exercice2_duree: tpl.exercice2_duree ?? 12,
      exercice3_duree: tpl.exercice3_duree ?? 12,
      logo_url: tpl.logo_url ?? '',
      primary_color: tpl.primary_color ?? '#313131',
      forme_juridique: tpl.forme_juridique ?? 'SASU',
      taux_is: tpl.taux_is ?? 25,
      taux_is_reduit: tpl.taux_is_reduit ?? 15,
      seuil_is_reduit: tpl.seuil_is_reduit ?? 42500,
      taux_pfu: tpl.taux_pfu ?? 30,
      default_taux_salarie: tpl.default_taux_salarie ?? 42,
      default_taux_dirigeant: tpl.default_taux_dirigeant ?? 45,
      ca_scenario_pct: tpl.ca_scenario_pct ?? 100,
      periode: tpl.periode,
      synthese_periode: tpl.synthese_periode,
      salaire_categories: tpl.salaire_categories ?? [],
    })
    .select()
    .single();

  if (fErr || !newForecast) {
    console.error('[Forecast] insert forecast failed', fErr);
    return null;
  }
  const nid = newForecast.id as string;

  const lineItems = cloneTemplateLineItems(nid, b);
  const rowErr = await persistClonedTemplateChildren(nid, b, lineItems);
  if (rowErr) {
    console.error('[Forecast] copy template rows failed', rowErr);
    return null;
  }

  const appState = appStateFromTemplateClone(newForecast as unknown as Record<string, unknown>, b, lineItems);

  return { id: nid, name: newForecast.name as string, appState };
}

// ─────────────────────────────────────────────────────────────
// AppState → DB rows (for save)
// ─────────────────────────────────────────────────────────────
function buildSavePayload(state: AppState, forecastId: string) {
  const forecastMeta = {
    treso_initiale: state.meta.tresoInitiale,
    societe_nom: state.meta.societeNom,
    societe_activite: state.meta.societeActivite,
    societe_marches: state.meta.societeMarches,
    societe_creation: state.meta.societeCreation || null,
    exercice1_duree: state.meta.exercice1Duree ?? 12,
    exercice2_duree: state.meta.exercice2Duree ?? 12,
    exercice3_duree: state.meta.exercice3Duree ?? 12,
    logo_url: state.meta.logoUrl ?? '',
    primary_color: state.meta.primaryColor ?? '#313131',
    forme_juridique: state.meta.formeJuridique ?? 'SASU',
    taux_is: state.meta.tauxIS ?? 25,
    taux_is_reduit: state.meta.tauxISReduit ?? 15,
    seuil_is_reduit: state.meta.seuilISReduit ?? 42500,
    taux_pfu: state.meta.tauxPFU ?? 30,
    default_taux_salarie: state.meta.defaultTauxSalarie ?? 42,
    default_taux_dirigeant: state.meta.defaultTauxDirigeant ?? 45,
    ca_scenario_pct: state.meta.caScenarioPct ?? 100,
    periode: state.ui.periode,
    synthese_periode: state.ui.synthesePeriode,
    dashboard_month_from: state.ui.dashboardMonthFrom,
    dashboard_month_to: state.ui.dashboardMonthTo,
    salaire_categories: state.salaireCategories,
    salaires_budget: state.salairesBudget ?? [],
    salaires_budget_mode: state.salairesBudgetMode ?? 'fixed',
    salaires_budget_pct: state.salairesBudgetPct ?? 0,
  };

  const revenus = state.revenus.map((r, i) => ({
    id: r.id,
    forecast_id: forecastId,
    libelle: r.libelle,
    categorie: r.categorie,
    mode: r.mode,
    montant_uniforme: r.montantUniforme,
    debut_mois: r.debutMois,
    montants_mensuels: r.montantsMensuels,
    montants_reels: r.montantsReels ?? [],
    prog_start: r.progStart,
    prog_base: r.progBase,
    prog_pct: r.progPct,
    prog_pct_mensuels: r.progPctMensuels,
    position: i,
    notes: r.notes ?? '',
  }));

  const chargesFlat = [
    ...state.charges.capex.map((c, i) => ({ cat: 'capex', c, i })),
    ...state.charges.marketing.map((c, i) => ({ cat: 'marketing', c, i })),
    ...state.charges.outils.map((c, i) => ({ cat: 'outils', c, i })),
    ...state.charges.tech.map((c, i) => ({ cat: 'tech', c, i })),
    ...state.charges.banque.map((c, i) => ({ cat: 'banque', c, i })),
    ...state.charges.legal.map((c, i) => ({ cat: 'legal', c, i })),
    ...state.charges.locaux.map((c, i) => ({ cat: 'locaux', c, i })),
    ...state.charges.vehicules.map((c, i) => ({ cat: 'vehicules', c, i })),
    ...state.charges.autres.map((c, i) => ({ cat: 'autres', c, i })),
  ].map(({ cat, c, i }) => ({
    id: c.id,
    forecast_id: forecastId,
    categorie: cat,
    libelle: c.libelle,
    mode: c.mode,
    montant_uniforme: c.montantUniforme,
    montants_mensuels: c.montantsMensuels,
    montants_reels: c.montantsReels ?? [],
    prog_start: c.progStart,
    prog_base: c.progBase,
    prog_pct: c.progPct,
    prog_pct_exercices: c.progPctExercices ?? null,
    variable_revenu_id: c.variableRevenuId ?? null,
    variable_charge_id: c.variableChargeId ?? null,
    variable_pct: c.variablePct ?? 0,
    variable_pct_exercices: c.variablePctExercices ?? null,
    variable_pct_mensuels: c.variablePctMensuels ?? null,
    fin_mois: c.finMois ?? null,
    deductible: c.deductible ?? true,
    taux_tva: c.tauxTVA ?? 20,
    position: i,
    notes: c.notes ?? '',
  }));

  const freelances = state.charges.freelances.map((f, i) => ({
    id: f.id,
    forecast_id: forecastId,
    nom: f.nom,
    mission: f.mission,
    mode: f.mode,
    montant_uniforme: f.montantUniforme,
    montants_mensuels: f.montantsMensuels,
    montants_reels: f.montantsReels ?? [],
    prog_start: f.progStart,
    prog_base: f.progBase,
    prog_pct: f.progPct,
    prog_pct_exercices: f.progPctExercices ?? null,
    variable_revenu_id: f.variableRevenuId ?? null,
    variable_charge_id: f.variableChargeId ?? null,
    variable_pct: f.variablePct ?? 0,
    variable_pct_exercices: f.variablePctExercices ?? null,
    variable_pct_mensuels: f.variablePctMensuels ?? null,
    fin_mois: f.finMois ?? null,
    deductible: f.deductible ?? true,
    taux_tva: f.tauxTVA ?? 20,
    position: i,
    notes: f.notes ?? '',
  }));

  const salaries = state.salaries.map((s, i) => ({
    id: s.id,
    forecast_id: forecastId,
    nom: s.nom,
    poste: s.poste,
    categorie_id: s.categorieId,
    mois_entree: s.moisEntree,
    mois_sortie: s.moisSortie,
    brut: s.brut,
    taux_patronal: s.tauxPatronal,
    custom_taux: s.customTaux,
    primes: s.primes,
    bruts_reels: s.brutsReels ?? [],
    position: i,
    notes: s.notes ?? '',
  }));

  const dirigeants = state.dirigeants.map((d, i) => ({
    id: d.id,
    forecast_id: forecastId,
    nom: d.nom,
    notes: d.notes ?? '',
    mode_saisie: d.modeSaisie,
    mode_net: d.modeSaisie === 'net',
    regime: d.regime,
    custom_taux: d.customTaux,
    remu_mode: d.remuMode,
    remu_uniform: d.remuUniform,
    remu_mensuels: d.remuMensuels,
    remu_reels: d.remuReels ?? [],
    dividendes: d.dividendes,
    position: i,
  }));

  const fiscalite = {
    forecast_id: forecastId,
    taux_reduit_pme: state.fiscalite.tauxReduitPME,
    cfe: state.fiscalite.cfe,
  };

  const autresTaxes = state.fiscalite.autresTaxes.map((t) => ({
    id: t.id,
    forecast_id: forecastId,
    label: t.label,
    montant: t.montant,
    type: t.type,
  }));

  const reel = {
    forecast_id: forecastId,
    ca: state.reel.ca,
    charges: state.reel.charges,
    masse_salariale: state.reel.masseSalariale,
    treso: state.reel.treso,
    // Overrides de la Synthèse (lignes agrégées) stockés dans une colonne jsonb unique
    // pour éviter d'ajouter ~15 colonnes. Clé = nom de la ligne (voir ReelOverrides).
    overrides: state.reelOverrides ?? null,
  };

  const investissements = (state.investissements ?? []).map((inv, i) => ({
    id: inv.id,
    forecast_id: forecastId,
    libelle: inv.libelle,
    mois_cible: inv.moisCible,
    montant: inv.montant,
    deductible: inv.deductible,
    taux_tva: inv.tauxTVA,
    notes: inv.notes ?? '',
    position: i,
  }));

  const notairesConfig = state.notairesConfig
    ? {
        forecast_id: forecastId,
        enabled: state.notairesConfig.enabled,
        panier_moyen: state.notairesConfig.panierMoyen,
        capacite_par_notaire: state.notairesConfig.capaciteParNotaire,
        salaire_notaire: state.notairesConfig.salaireNotaire,
        notaires_initiaux: state.notairesConfig.notairesInitiaux,
        seuil_recrutement_pct: state.notairesConfig.seuilRecrutementPct,
        delai_recrutement: state.notairesConfig.delaiRecrutement,
        cout_partenaire_pct: state.notairesConfig.coutPartenairePct,
        seuil_head_ops: state.notairesConfig.seuilHeadOps,
        salaire_head_ops: state.notairesConfig.salaireHeadOps,
        ratio_assistant: state.notairesConfig.ratioAssistant,
        salaire_assistant: state.notairesConfig.salaireAssistant,
        sous_traitance_reels: state.notairesConfig.sousTraitanceReels ?? null,
        updated_at: new Date().toISOString(),
      }
    : null;

  return { forecastMeta, revenus, chargesFlat, freelances, salaries, dirigeants, fiscalite, autresTaxes, reel, investissements, notairesConfig };
}

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────
export function ForecastProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { state, dispatch } = useAppState();
  const [forecastId, setForecastId] = useState<string | null>(null);
  const [forecastName, setForecastName] = useState('Mon prévisionnel');
  const [forecastDescription, setForecastDescription] = useState('');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [loading, setLoading] = useState(true);
  const [scenarios, setScenarios] = useState<ScenarioMeta[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Détecte le rôle super_admin (accès total, ignore le niveau d'accès)
  useEffect(() => {
    if (!user) {
      setIsSuperAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('is_super_admin');
      if (!cancelled) setIsSuperAdmin(Boolean(data));
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Niveau d'accès effectif sur le scénario actif
  const currentAccessLevel = useMemo<'owner' | 'viewer' | 'editor' | 'admin'>(() => {
    if (!forecastId) return 'owner';
    const active = scenarios.find((s) => s.id === forecastId);
    return active?.accessLevel ?? 'owner';
  }, [forecastId, scenarios]);

  // canWrite = owner, editor, admin, ou super_admin (bypass)
  const canWrite = useMemo(
    () => isSuperAdmin || currentAccessLevel !== 'viewer',
    [isSuperAdmin, currentAccessLevel],
  );

  // ── Load scenarios list ────────────────────────────────────
  // Inclut les forecasts dont l'user est propriétaire ET ceux partagés via
  // scenario_access. La RLS filtre déjà le SELECT ; on ajoute juste les infos
  // owner/accessLevel côté client.
  const loadScenarios = useCallback(async (uid: string) => {
    setScenariosLoading(true);
    const [{ data: forecasts }, { data: accesses }] = await Promise.all([
      supabase
        .from('forecasts')
        .select('id, name, description, updated_at, user_id')
        .eq('is_template', false)
        .order('updated_at', { ascending: false }),
      supabase
        .from('scenario_access')
        .select('forecast_id, access_level')
        .eq('user_id', uid),
    ]);

    const accessMap = new Map<string, 'viewer' | 'editor' | 'admin'>();
    (accesses ?? []).forEach((a) => {
      accessMap.set(
        a.forecast_id as string,
        (a.access_level as 'viewer' | 'editor' | 'admin') ?? 'viewer',
      );
    });

    setScenarios(
      (forecasts ?? []).map((r) => {
        const isOwner = (r.user_id as string) === uid;
        const sharedLevel = accessMap.get(r.id as string);
        return {
          id: r.id as string,
          name: r.name as string,
          description: (r.description as string) ?? '',
          updatedAt: r.updated_at as string,
          isOwner,
          accessLevel: isOwner ? 'owner' : (sharedLevel ?? 'viewer'),
        };
      }),
    );
    setScenariosLoading(false);
  }, []);

  // ── Seed 1:1 rows for a brand-new empty forecast ──────────
  const seedEmptyForecastRows = useCallback(async (forecastRowId: string, emptyState: ReturnType<typeof createEmptyAppState>) => {
    const d0 = emptyState.dirigeants[0];
    await Promise.all([
      supabase.from('dirigeants').insert({
        id: d0.id,
        forecast_id: forecastRowId,
        nom: d0.nom,
        notes: d0.notes ?? '',
        mode_saisie: d0.modeSaisie,
        mode_net: d0.modeSaisie === 'net',
        regime: d0.regime,
        custom_taux: d0.customTaux,
        remu_mode: d0.remuMode,
        remu_uniform: d0.remuUniform,
        remu_mensuels: d0.remuMensuels,
        remu_reels: d0.remuReels ?? [],
        dividendes: d0.dividendes,
        position: 0,
      }),
      supabase.from('fiscalite').insert({
        forecast_id: forecastRowId,
        taux_reduit_pme: emptyState.fiscalite.tauxReduitPME,
        cfe: emptyState.fiscalite.cfe,
      }),
      supabase.from('reel').insert({
        forecast_id: forecastRowId,
        ca: emptyState.reel.ca,
        charges: emptyState.reel.charges,
        masse_salariale: emptyState.reel.masseSalariale,
        treso: emptyState.reel.treso,
      }),
    ]);
  }, []);

  // ── Load or create forecast on login ──────────────────────
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);

      // Check if user already has a forecast
      const { data: existing } = await supabase
        .from('forecasts')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_template', false)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        // ⚠️ Ne pas définir forecastId avant que le chargement réussisse.
        // Si fetchForecastData retourne null (cache PostgREST périmé), l'autosave
        // ne doit PAS se déclencher avec un état vide (ce qui effacerait les données en DB).
        const fetchedState = await fetchForecastData(existing as unknown as Record<string, unknown>);
        if (fetchedState === null) {
          console.error('[Forecast] Could not load forecast data — DB query failed. Execute: NOTIFY pgrst, \'reload schema\'; in Supabase SQL Editor, then refresh this page.');
          toast.error(
            '⚠️ Erreur de chargement des données. Exécutez "NOTIFY pgrst, \'reload schema\';" dans Supabase SQL Editor puis rechargez la page.',
            { duration: 20000, id: 'load-error' },
          );
          setLoading(false);
          return;
        }
        // Chargement réussi → on peut maintenant fixer l'id (l'autosave peut s'activer)
        setForecastId(existing.id as string);
        setForecastName(existing.name as string);
        setForecastDescription((existing.description as string) ?? '');

        let appState = fetchedState;
        if (isShellWithoutLineItems(appState)) {
          const repaired = await refillForecastFromTemplate(existing.id as string, appState.ui);
          if (repaired) appState = repaired;
        }
        dispatch({ type: 'STATE_LOAD', state: appState });
      } else {
        // First login: copy template into a new user forecast
        const result = await copyTemplateForUser(user.id);
        if (result) {
          setForecastId(result.id);
          setForecastName(result.name);
          setForecastDescription('');
          dispatch({ type: 'STATE_LOAD', state: result.appState });
        } else {
          // Fallback: create empty forecast
          const { data: empty } = await supabase
            .from('forecasts')
            .insert({ user_id: user.id, name: 'Mon prévisionnel', description: '', is_template: false })
            .select()
            .single();
          if (empty) {
            setForecastId(empty.id as string);
            setForecastName(empty.name as string);
            setForecastDescription('');
            const emptyState = createEmptyAppState();
            await seedEmptyForecastRows(empty.id as string, emptyState);
            dispatch({ type: 'STATE_LOAD', state: emptyState });
          }
        }
      }

      await loadScenarios(user.id);
      setLoading(false);
    };

    load();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save all tables ────────────────────────────────────────
  const saveNow = useCallback(async () => {
    if (!forecastId || !user) return;
    // Lecture seule : on n'envoie aucune requête qui serait rejetée par la RLS.
    if (!canWrite) {
      setSaveStatus('idle');
      return;
    }
    setSaveStatus('saving');

    const { forecastMeta, revenus, chargesFlat, freelances, salaries, dirigeants, fiscalite, autresTaxes, reel, investissements, notairesConfig } =
      buildSavePayload(state, forecastId);

    try {
      // Update forecast meta
      const { error: metaErr } = await supabase
        .from('forecasts')
        .update({ ...forecastMeta, updated_at: new Date().toISOString() })
        .eq('id', forecastId);
      if (metaErr) console.error('[AutoSave] forecasts update error:', metaErr);

      // Safe sync: UPSERT current rows first, then delete orphans.
      const syncTable = async (table: string, rows: Record<string, unknown>[], optional = false) => {
        if (rows.length) {
          const { error: upsertErr } = await supabase
            .from(table)
            .upsert(rows, { onConflict: 'id' });
          if (upsertErr) {
            if (optional) {
              console.warn(`[AutoSave] UPSERT skipped (${table}, table may not exist yet):`, upsertErr.message);
              return;
            }
            console.error(`[AutoSave] UPSERT failed (${table}):`, upsertErr);
            throw new Error(`Erreur sauvegarde (${table}) : ${upsertErr.message}`);
          }
          const currentIds = rows.map((r) => r.id as string);
          await supabase
            .from(table)
            .delete()
            .eq('forecast_id', forecastId)
            .not('id', 'in', `(${currentIds.map((id) => `"${id}"`).join(',')})`);
        } else {
          await supabase.from(table).delete().eq('forecast_id', forecastId);
        }
      };

      await Promise.all([
        syncTable('revenus', revenus as unknown as Record<string, unknown>[]),
        syncTable('charges', chargesFlat as unknown as Record<string, unknown>[]),
        syncTable('freelances', freelances as unknown as Record<string, unknown>[]),
        syncTable('salaries', salaries as unknown as Record<string, unknown>[]),
        syncTable('dirigeants', dirigeants as unknown as Record<string, unknown>[]),
        syncTable('autres_taxes', autresTaxes as unknown as Record<string, unknown>[]),
        syncTable('investissements', investissements as unknown as Record<string, unknown>[], true),
        supabase.from('fiscalite').upsert(fiscalite, { onConflict: 'forecast_id' }),
        supabase.from('reel').upsert(reel, { onConflict: 'forecast_id' }),
        // Table optionnelle : ignore silencieusement si le patch SQL n'a pas encore été exécuté.
        notairesConfig
          ? supabase
              .from('notaires_config')
              .upsert(notairesConfig, { onConflict: 'forecast_id' })
              .then((res) => {
                if (res.error) {
                  console.warn(
                    '[AutoSave] notaires_config UPSERT skipped (run supabase-patch-notaires.sql):',
                    res.error.message,
                  );
                }
                return res;
              })
          : Promise.resolve({ error: null }),
      ]);

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('[AutoSave] Save failed:', err);
      setSaveStatus('error');
      toast.error(
        err instanceof Error ? err.message : 'Erreur de sauvegarde — vérifiez la console',
        { duration: 8000, id: 'save-error' },
      );
    }
  }, [forecastId, user, state, canWrite]);

  // ── Refresh from DB (overrides in-memory state with fresh DB data) ──
  const refreshFromDB = useCallback(async () => {
    if (!forecastId) return;
    const { data: forecastRow } = await supabase
      .from('forecasts')
      .select('*')
      .eq('id', forecastId)
      .single();
    if (!forecastRow) return;
    const freshState = await fetchForecastData(forecastRow as unknown as Record<string, unknown>);
    if (freshState) {
      dispatch({ type: 'STATE_LOAD', state: freshState });
      toast.success('Données rechargées depuis la base');
    } else {
      toast.error('Échec du rechargement — vérifiez la console');
    }
  }, [forecastId, dispatch]);

  // ── Rename (current scenario) ──────────────────────────────
  const rename = async (name: string) => {
    if (!forecastId || !user) return;
    if (!canWrite) {
      toast.error('Accès en lecture seule sur ce scénario');
      return;
    }
    setForecastName(name);
    await supabase.from('forecasts').update({ name }).eq('id', forecastId);
    await loadScenarios(user.id);
  };

  // ── Reset to template ──────────────────────────────────────
  const resetForecast = async () => {
    if (!forecastId || !user) return;
    if (!canWrite) {
      toast.error('Accès en lecture seule sur ce scénario');
      return;
    }
    const appState = await refillForecastFromTemplate(forecastId, state.ui);
    if (appState) dispatch({ type: 'STATE_LOAD', state: appState });
  };

  // ── Switch to another scenario ─────────────────────────────
  const switchScenario = useCallback(async (id: string) => {
    if (id === forecastId) return;
    setLoading(true);
    const { data: forecastRow } = await supabase
      .from('forecasts')
      .select('*')
      .eq('id', id)
      .single();
    if (!forecastRow) {
      toast.error('Scénario introuvable');
      setLoading(false);
      return;
    }
    const fetchedState = await fetchForecastData(forecastRow as unknown as Record<string, unknown>);
    if (fetchedState === null) {
      toast.error('Erreur de chargement du scénario — vérifiez la console');
      setLoading(false);
      return;
    }
    setForecastId(id);
    setForecastName(forecastRow.name as string);
    setForecastDescription((forecastRow.description as string) ?? '');
    dispatch({ type: 'STATE_LOAD', state: fetchedState });
    setLoading(false);
    toast.success(`Scénario "${forecastRow.name}" activé`);
  }, [forecastId, dispatch]);

  // ── Create a scenario (blank or copied from another) ──────
  const createScenario = useCallback(async (name: string, description: string, copyFromId?: string) => {
    if (!user) return;

    if (copyFromId) {
      // ── Mode copie ──────────────────────────────────────────
      const { data: srcForecast } = await supabase
        .from('forecasts')
        .select('*')
        .eq('id', copyFromId)
        .single();

      if (!srcForecast) {
        toast.error('Scénario source introuvable');
        return;
      }

      // Créer le nouveau forecast en reprenant les meta du source
      const { data: newForecast, error } = await supabase
        .from('forecasts')
        .insert({
          user_id: user.id,
          name,
          description,
          is_template: false,
          treso_initiale: srcForecast.treso_initiale,
          societe_nom: srcForecast.societe_nom,
          societe_activite: srcForecast.societe_activite,
          societe_marches: srcForecast.societe_marches,
          societe_creation: srcForecast.societe_creation,
          exercice1_duree: srcForecast.exercice1_duree,
          exercice2_duree: srcForecast.exercice2_duree,
          exercice3_duree: srcForecast.exercice3_duree,
          ca_scenario_pct: srcForecast.ca_scenario_pct ?? 100,
          salaire_categories: srcForecast.salaire_categories,
        })
        .select()
        .single();

      if (error || !newForecast) {
        toast.error('Impossible de créer le scénario');
        return;
      }

      const newId = newForecast.id as string;

      // Copier les tables avec lignes (génère de nouveaux UUIDs pour chaque ligne)
      const tablesToCopy = ['revenus', 'charges', 'freelances', 'salaries', 'dirigeants', 'autres_taxes'];
      await Promise.all(
        tablesToCopy.map(async (table) => {
          const { data: rows } = await supabase.from(table).select('*').eq('forecast_id', copyFromId);
          if (rows && rows.length > 0) {
            const cloned = rows.map((r) => ({ ...r, id: crypto.randomUUID(), forecast_id: newId }));
            await supabase.from(table).insert(cloned);
          }
        }),
      );

      // Table investissements (optionnelle)
      const { data: invRows } = await supabase.from('investissements').select('*').eq('forecast_id', copyFromId);
      if (invRows && invRows.length > 0) {
        const cloned = invRows.map((r) => ({ ...r, id: crypto.randomUUID(), forecast_id: newId }));
        await supabase.from('investissements').insert(cloned).then(({ error: e }) => {
          if (e) console.warn('[createScenario] investissements copy skipped:', e.message);
        });
      }

      // Table notaires_config (optionnelle, 1:1)
      const { data: notCfgRow } = await supabase
        .from('notaires_config')
        .select('*')
        .eq('forecast_id', copyFromId)
        .maybeSingle();
      if (notCfgRow) {
        const { forecast_id: _fid, ...rest } = notCfgRow as Record<string, unknown>;
        void _fid;
        await supabase
          .from('notaires_config')
          .upsert({ ...rest, forecast_id: newId }, { onConflict: 'forecast_id' })
          .then(({ error: e }) => {
            if (e) console.warn('[createScenario] notaires_config copy skipped:', e.message);
          });
      }

      // Tables à ligne unique (fiscalite, reel, dirigeant legacy)
      const singleTables = ['fiscalite', 'reel'];
      await Promise.all(
        singleTables.map(async (table) => {
          const { data: row } = await supabase.from(table).select('*').eq('forecast_id', copyFromId).maybeSingle();
          if (row) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id: _id, ...rest } = row as Record<string, unknown>;
            await supabase.from(table).upsert({ ...rest, forecast_id: newId });
          }
        }),
      );

      const { data: legRow } = await supabase.from('dirigeant').select('*').eq('forecast_id', copyFromId).maybeSingle();
      if (legRow) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _id, ...rest } = legRow as Record<string, unknown>;
        await supabase.from('dirigeant').upsert({ ...rest, forecast_id: newId });
      }

      await loadScenarios(user.id);
      await switchScenario(newId);
      toast.success(`Scénario "${name}" créé (copie)`);
    } else {
      // ── Mode vide ───────────────────────────────────────────
      const { data: newForecast, error } = await supabase
        .from('forecasts')
        .insert({ user_id: user.id, name, description, is_template: false })
        .select()
        .single();
      if (error || !newForecast) {
        toast.error('Impossible de créer le scénario');
        return;
      }
      const emptyState = createEmptyAppState();
      await seedEmptyForecastRows(newForecast.id as string, emptyState);
      await loadScenarios(user.id);
      await switchScenario(newForecast.id as string);
      toast.success(`Scénario "${name}" créé`);
    }
  }, [user, seedEmptyForecastRows, loadScenarios, switchScenario]);

  // ── Delete a scenario ──────────────────────────────────────
  const deleteScenario = useCallback(async (id: string) => {
    if (!user) return;
    if (scenarios.length <= 1) {
      toast.error('Impossible de supprimer le dernier scénario');
      return;
    }
    const { error } = await supabase
      .from('forecasts')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) {
      toast.error('Erreur lors de la suppression');
      return;
    }
    const remaining = scenarios.filter((s) => s.id !== id);
    await loadScenarios(user.id);
    if (id === forecastId && remaining.length > 0) {
      await switchScenario(remaining[0].id);
    }
    toast.success('Scénario supprimé');
  }, [user, scenarios, forecastId, loadScenarios, switchScenario]);

  // ── Update scenario name + description ────────────────────
  const updateScenarioMeta = useCallback(async (id: string, name: string, description: string) => {
    if (!user) return;
    const { error } = await supabase
      .from('forecasts')
      .update({ name, description })
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) {
      toast.error('Erreur lors de la mise à jour');
      return;
    }
    if (id === forecastId) {
      setForecastName(name);
      setForecastDescription(description);
    }
    await loadScenarios(user.id);
  }, [user, forecastId, loadScenarios]);

  return (
    <ForecastContext.Provider value={{
      forecastId,
      forecastName,
      forecastDescription,
      saveStatus,
      loading,
      scenarios,
      scenariosLoading,
      canWrite,
      currentAccessLevel,
      saveNow,
      refreshFromDB,
      rename,
      resetForecast,
      switchScenario,
      createScenario,
      deleteScenario,
      updateScenarioMeta,
    }}>
      {children}
    </ForecastContext.Provider>
  );
}

